/**
 * PATH: packages/email-hub/src/feature/signal-strip/laneMenuActions.ts
 * FILE_ID: EMAILHUB-FEATURE-SIGNALSTRIP-LANEMENUACTIONS-001
 * PURPOSE: Pure inventory of actionable items for reader signal-lane menus.
 * WHY: BUG-2026-07-17-EMAIL-LANE-DEAD-CONTEXTMENU — a lane must never
 *      intercept right-click only to show a disabled stub. Callers fall
 *      through to the email context menu when this returns false.
 * RELATED: ReaderSignalStripMenus.tsx, ReaderSignalStrip.tsx
 */

export type ReaderSignalLane = 'who' | 'what' | 'signals' | 'where'

export interface LaneMenuActionContext {
  hasCompanyDomain: boolean
  canShowCompanyEmails: boolean
  hasActiveVerification: boolean
  hasAiSummary: boolean
  canRequestRule: boolean
  hasFiledCard: boolean
  hasFiledBoard: boolean
}

/** Count only clickable (non-disabled) lane-menu actions. */
export function countLaneMenuActions(
  lane: ReaderSignalLane,
  ctx: LaneMenuActionContext,
): number {
  switch (lane) {
    case 'who': {
      let count = 1 // Copy company name is always offered
      if (ctx.hasCompanyDomain) count += 2 // Open company context + copy domain
      if (ctx.canShowCompanyEmails) count += 1
      return count
    }
    case 'what': {
      let count = 1 // Ask AI
      if (ctx.canRequestRule) count += 1
      return count
    }
    case 'signals': {
      // Ask AI is always offered so Intelligence never opens as a dead stub
      // (verification-only menus used to show "No active verification code").
      let count = 1
      if (ctx.hasActiveVerification) count += 2 // Copy code + Not a code
      if (ctx.hasAiSummary) count += 1
      return count
    }
    case 'where': {
      let count = 1 // Explain this filing (Ask AI)
      if (ctx.hasFiledCard) count += 1
      if (ctx.hasFiledBoard) count += 1
      if (ctx.canRequestRule) count += 1
      return count
    }
    default:
      return 0
  }
}

export function laneMenuHasActions(
  lane: ReaderSignalLane,
  ctx: LaneMenuActionContext,
): boolean {
  return countLaneMenuActions(lane, ctx) > 0
}

export function buildLaneMenuActionContext(input: {
  companyDomain?: string | null
  canShowCompanyEmails: boolean
  hasActiveVerification: boolean
  aiSummary?: string | null
  canRequestRule: boolean
  filedCardId?: string | null
  filedBoardId?: string | null
}): LaneMenuActionContext {
  return {
    hasCompanyDomain: Boolean(input.companyDomain),
    canShowCompanyEmails: input.canShowCompanyEmails,
    hasActiveVerification: input.hasActiveVerification,
    hasAiSummary: Boolean(input.aiSummary?.trim()),
    canRequestRule: input.canRequestRule,
    hasFiledCard: Boolean(input.filedCardId),
    hasFiledBoard: Boolean(input.filedBoardId),
  }
}
